// Session state for the assistant panel — the slide-out that replaced the
// full-page /chat screen. Mounted once in OperatorLayout so the conversation
// survives navigation between operator screens; the panel itself stays mounted
// (hidden) while closed for the same reason.
//
// Three things live here and nowhere else:
//  - open/closed state (persisted for wide screens, where the panel docks)
//  - the ask queue: `ask(text)` opens the panel and auto-sends one message —
//    the calendar's Pay handoff and the campaign/sequence "ask innoAI" buttons
//    use it instead of navigating to a chat page
//  - the page context: which screen the operator has open, published by the
//    richer screens (Schedule, CustomerDetail, BookingDetail) and derived from
//    the route for everything else. The panel appends it to the next sent
//    message (lib/pageContext.ts) so the AI knows what "this customer" or
//    "today" means.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router'
import { contextKey, type PageContext } from '@/lib/pageContext'
import { ALL_NAV_ITEMS } from '@/components/nav/navItems'

const OPEN_STORAGE_KEY = 'assistant-panel-open'
/** Above this width the panel docks beside the content instead of overlaying it. */
export const PANEL_DOCK_BREAKPOINT = 1280

interface AssistantPanelState {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
  /** Opens the panel and auto-sends `text` as the operator's next message. */
  ask: (text: string) => void
  /** Consumed by the panel: pops the oldest queued ask (null when none). */
  takeAsk: () => string | null
  /** Bumped on every ask() so the panel's drain effect re-runs. */
  askVersion: number
  /** What the operator has open right now (published, or route-derived). */
  pageContext: PageContext | null
  /** Pages with richer context than their route publish it through this. */
  publishPageContext: (ctx: PageContext | null) => void
}

const NOOP_STATE: AssistantPanelState = {
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  ask: () => {},
  takeAsk: () => null,
  askVersion: 0,
  pageContext: null,
  publishPageContext: () => {},
}

const AssistantPanelContext = createContext<AssistantPanelState>(NOOP_STATE)

export function useAssistantPanel(): AssistantPanelState {
  return useContext(AssistantPanelContext)
}

/**
 * Fallback context for screens that publish nothing: the nav item's own label
 * and hint. Detail routes (customer/booking/…) resolve to nothing here — the
 * big three publish their own richer context, and a wrong guess ("Customers"
 * while a specific record is open) is worse than none.
 */
function deriveRouteContext(pathname: string): PageContext | null {
  // '/chat' is the legacy redirect route, not a screen anyone stays on.
  if (pathname === '/' || pathname === '/chat') return null
  const item = ALL_NAV_ITEMS.find((i) => i.href === pathname)
  if (!item) return null
  return { kind: 'screen', label: item.label, hint: item.hint }
}

function restoreOpen(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    // Only restore an open panel where it docks — auto-opening a full-screen
    // overlay on a phone would cover the screen the operator navigated to.
    if (!window.matchMedia(`(min-width: ${PANEL_DOCK_BREAKPOINT}px)`).matches) return false
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function AssistantPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(restoreOpen)
  const [askVersion, setAskVersion] = useState(0)
  const [published, setPublished] = useState<PageContext | null>(null)
  const askQueue = useRef<string[]>([])
  const { pathname } = useLocation()

  useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_STORAGE_KEY, isOpen ? '1' : '0')
    } catch {
      // storage unavailable (private mode) — the panel just starts closed
    }
  }, [isOpen])

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])

  // The queue is a ref so the panel's StrictMode-doubled drain effect stays
  // idempotent: the second run shifts an empty queue and does nothing.
  const ask = useCallback((text: string) => {
    if (!text.trim()) return
    askQueue.current.push(text)
    setAskVersion((v) => v + 1)
    setIsOpen(true)
  }, [])

  const takeAsk = useCallback(() => askQueue.current.shift() ?? null, [])

  const publishPageContext = useCallback((ctx: PageContext | null) => {
    setPublished(ctx)
  }, [])

  const pageContext = published ?? deriveRouteContext(pathname)

  const value = useMemo<AssistantPanelState>(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      ask,
      takeAsk,
      askVersion,
      pageContext,
      publishPageContext,
    }),
    [isOpen, open, close, toggle, ask, takeAsk, askVersion, pageContext, publishPageContext],
  )

  return <AssistantPanelContext.Provider value={value}>{children}</AssistantPanelContext.Provider>
}

/**
 * Publish what this screen is showing so the assistant knows what "this
 * customer" / "today" means. Pass null (or unmount) to fall back to the
 * route-derived label. Keyed on contextKey so object literals in render
 * don't republish every render.
 */
export function useAssistantPageContext(ctx: PageContext | null) {
  const { publishPageContext } = useAssistantPanel()
  const key = contextKey(ctx)
  const ctxRef = useRef(ctx)
  ctxRef.current = ctx
  useEffect(() => {
    publishPageContext(ctxRef.current)
    return () => publishPageContext(null)
  }, [publishPageContext, key])
}
